import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTION_LABEL = {
  "login": "signed in",
  "logout": "signed out",
  "user.create": "created a user",
  "user.update": "updated a user",
  "user.delete": "soft-deleted a user",
  "user.restore": "restored a user",
  "user.reset_password": "reset a user password",
  "profile.update": "updated their profile",
  "profile.change_password": "changed their password",
  "org.create": "created an organization",
  "department.create": "created a department",
  "department.update": "updated a department",
  "department.delete": "soft-deleted a department",
  "department.restore": "restored a department",
  "application.create": "created an application",
  "application.update": "updated an application",
  "application.delete": "soft-deleted an application",
  "application.restore": "restored an application",
  "module.create": "added a module",
  "module.update": "updated a module",
  "module.delete": "deleted a module",
  "module.reorder": "reordered modules",
  "sla.create": "created an SLA policy",
  "sla.update": "updated an SLA policy",
  "sla.delete": "deleted an SLA policy",
  "sla.soft_delete": "soft-deleted an SLA policy",
  "settings.update": "updated system settings",
  "business_hours.update": "updated business hours",
  "holiday.create": "added a holiday",
  "holiday.delete": "removed a holiday",
  "role.permissions.update": "updated role permissions",
  "ticket.create": "created a ticket",
  "ticket.edit": "edited ticket fields",
  "ticket.status": "changed ticket status",
  "ticket.reopen_request": "requested a ticket reopen",
  "ticket.reopen_decision": "decided on a reopen request",
  "comment.add": "added a comment",
  "attachment.upload": "uploaded an attachment",
  "approval.request": "requested an approval",
  "approval.decide": "decided on an approval",
};

function humanizeValue(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "string") return v.replace(/_/g, " ");
  if (Array.isArray(v)) return v.map(humanizeValue).join(", ");
  if (typeof v === "object") {
    return Object.entries(v)
      .filter(([k]) => !["ip_address", "id"].includes(k))
      .map(([k, val]) => `${k.replace(/_/g, " ")}: ${humanizeValue(val)}`)
      .join(" · ");
  }
  return String(v);
}

function tone(action) {
  if (action.endsWith(".delete") || action.endsWith(".soft_delete")) return "text-rose-300 border-rose-500/30 bg-rose-500/10";
  if (action.endsWith(".create") || action.endsWith(".add")) return "text-emerald-300 border-emerald-500/30 bg-emerald-500/10";
  if (action.endsWith(".update") || action === "profile.update" || action === "ticket.edit") return "text-[#e5c158] border-[#d4af37]/30 bg-[#d4af37]/10";
  if (action.endsWith(".restore")) return "text-sky-300 border-sky-500/30 bg-sky-500/10";
  return "text-muted-foreground border-border";
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    api.get("/audit-logs", { params: { limit: 500 } }).then((r) => setLogs(r.data || []));
  }, []);

  const filtered = logs.filter((l) => {
    if (actionFilter && !l.action?.startsWith(actionFilter)) return false;
    if (q) {
      const s = q.toLowerCase();
      const hit = [l.user_email, l.action, l.entity, l.entity_id].some((x) => (x || "").toLowerCase().includes(s));
      if (!hit) return false;
    }
    return true;
  });

  const actionGroups = Array.from(new Set(logs.map((l) => (l.action || "").split(".")[0]).filter(Boolean))).sort();

  return (
    <div className="space-y-4" data-testid="admin-audit-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Audit log</h1>
        <p className="text-sm text-muted-foreground">Every meaningful change captured with who, when and what changed.</p>
      </div>

      <div className="flex gap-3 flex-wrap items-center rounded-xl border border-border bg-card/60 p-3">
        <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by user, action, entity…" className="max-w-sm" />
        <Select value={actionFilter} onValueChange={(v) => setActionFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="audit-action-filter" className="w-52"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All actions</SelectItem>
            {actionGroups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">{filtered.length} events</div>
      </div>

      <div className="rounded-xl border border-border bg-card/70 divide-y divide-border">
        <div className="hidden md:grid grid-cols-[6.5rem_minmax(0,1fr)] md:grid-cols-[7.5rem_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div>Action</div>
          <div>Details</div>
          <div className="text-right">Time</div>
        </div>
        {filtered.map((l) => (
          <div key={l.id} data-testid={`audit-row-${l.id}`} className="grid grid-cols-[6.5rem_minmax(0,1fr)] md:grid-cols-[7.5rem_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 px-4 py-3 hover:bg-white/[0.03]">
            <div className="pt-px">
              <span className={`inline-flex w-full justify-center truncate text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 ${tone(l.action || "")}`}>
                {(l.action || "").split(".")[0]}
              </span>
            </div>
            <div className="min-w-0 break-words">
              <div className="text-sm">
                <span className="font-semibold">{l.user_email || "system"}</span>{" "}
                <span className="text-muted-foreground">{ACTION_LABEL[l.action] || (l.action || "").replace(/_/g, " ").replace(".", " → ")}</span>
                {l.entity_id && <span className="text-muted-foreground"> on {l.entity} <span className="font-mono text-[11px] text-[#e5c158]/80">{l.entity_id.slice(0, 8)}</span></span>}
              </div>
              {(l.previous_value != null || l.new_value != null) && (
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {l.previous_value != null && <><span className="text-rose-300/80">before:</span> {humanizeValue(l.previous_value)} · </>}
                  {l.new_value != null && <><span className="text-emerald-300/80">after:</span> {humanizeValue(l.new_value)}</>}
                </div>
              )}
            </div>
            <div className="col-start-2 md:col-start-auto md:pt-0.5 md:text-right text-[11px] text-muted-foreground font-mono whitespace-nowrap">{formatDateTime(l.created_at)}</div>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-10 text-center text-muted-foreground text-xs">No matching audit events.</div>}
      </div>
    </div>
  );
}
